"use client";

import { useDialogPreviewTarget } from "@/components/ui/shadcn/dialog-preview";
import { Button } from "@/components/ui/shadcn/button";
import { cn } from "@/utils/cn";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { X } from "lucide-react";
import * as React from "react";

import { memo } from "react";

/**
 * Dialog — shadcn primitive migrated to class-selector + co-located CSS.
 *
 * REFERENCE PATTERN: class-selector + co-located CSS (see shadcn-authoring.md
 * commit b35c0a615). Ninth execution of the shadcn-authoring template;
 * second Tier-3 (modal) primitive after AlertDialog (commit 320fa436b).
 *
 * 10 sub-components total; 6 styled identities + 4 verbatim re-exports:
 *   Re-exports:  Dialog (Root), DialogTrigger, DialogPortal, DialogClose
 *   Styled:      DialogOverlay, DialogContent, DialogHeader, DialogFooter,
 *                DialogTitle, DialogDescription
 *
 * Internal BEM sub-elements within DialogContent:
 *   __close      — the close control's place at the top-right corner; the control is the
 *                  shared ghost icon Button (Dylan, 2026-10-10), so it looks and responds as
 *                  every icon button does
 *
 * Animation strategy mirrors AlertDialog (commit 320fa436b) and Select
 * (commit 4af955690). Token-discipline win same as AlertDialog: the
 * pre-migration `duration-200` (200ms) literal substituted with
 * var(--duration-base) (= 150ms).
 *
 * Documented Frankenstein on DialogTitle: heading-section font-size +
 * heading-section font-weight + leading-none override + tight letter-
 * spacing. Same shape as CardTitle precedent (commit 72d5076c0).
 *
 * Public API preserved EXACTLY: every Radix import + integration verbatim;
 * all forwardRef + displayName preserved; all 10 exports unchanged.
 *
 * Source: docs/implementation-blueprint.md §8.3 (class-selector pattern);
 *         .claude/agent-prompts/shadcn-authoring.md (binding template, commit b35c0a615);
 *         .claude/agent-prompts/atom-authoring-class-selector.md (shared rules);
 *         src/components/ui/shadcn/alert-dialog.tsx (Tier-3 modal precedent, commit 320fa436b);
 *         src/components/ui/shadcn/select.tsx (Tier-2 animation precedent, commit 4af955690);
 *         src/components/ui/shadcn/card.tsx (CardTitle Frankenstein precedent, commit 72d5076c0).
 */

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = memo(
    React.forwardRef<React.ComponentRef<typeof DialogPrimitive.Overlay>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>>(({ className, ...props }, ref) => <DialogPrimitive.Overlay ref={ref} className={cn("dw-dialog-overlay", className)} data-component="DialogOverlay" data-location="src/components/ui/shadcn/dialog.tsx" {...props} />)
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = memo(
    React.forwardRef<React.ComponentRef<typeof DialogPrimitive.Content>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
        /** Max width: sm (--measure-sm), md (--measure-md), lg (--measure-lg), xl (--measure-xl). @default the page measure */
        size?: "sm" | "md" | "lg" | "xl";
        /** The close control's accessible name. @default "Close" */
        closeLabel?: string;
    }>(({ className, children, size, closeLabel = "Close", ...props }, ref) => {
        // Editor-emulator seam (see dialog-preview.tsx). In a preview frame the
        // content renders as a PLAIN inert div, in place — no DialogPortal, no
        // DialogPrimitive.Content at all. The Radix machinery buys nothing here
        // (the content is inert decoration: no focus trap, no dismiss, no portal)
        // and its dev-time accessibility probe getElementById()s the PARENT
        // document while the title lives in the frame's — every preview selection
        // popped a false "DialogContent requires a DialogTitle" into the overlay.
        // Rendering in place also keeps the subtree inside the frame's guarded
        // layout root. Title/Close children still resolve their Radix context from
        // the Dialog root, which remains mounted.
        const preview = useDialogPreviewTarget();
        if (preview) {
            return (
                <div ref={ref as React.Ref<HTMLDivElement>} className={cn("dw-dialog-content", className)} data-size={size} inert data-component="DialogContent" data-location="src/components/ui/shadcn/dialog.tsx">
                    {children}
                </div>
            );
        }
        return (
            <DialogPortal data-component="DialogContent" data-location="src/components/ui/shadcn/dialog.tsx">
                <DialogOverlay />
                <DialogPrimitive.Content ref={ref} className={cn("dw-dialog-content", className)} data-size={size} {...props}>
                    {children}
                    <DialogPrimitive.Close asChild>
                        <Button variant="ghost" size="icon" className="dw-dialog-content__close" aria-label={closeLabel}>
                            <X />
                        </Button>
                    </DialogPrimitive.Close>
                </DialogPrimitive.Content>
            </DialogPortal>
        );
    })
);

DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = memo(
    function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
        return <div className={cn("dw-dialog-header", className)} data-component="DialogHeader" data-location="src/components/ui/shadcn/dialog.tsx" {...props} />;
    }
);

DialogHeader.displayName = "DialogHeader";

const DialogFooter = memo(
    function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
        return <div className={cn("dw-dialog-footer", className)} data-component="DialogFooter" data-location="src/components/ui/shadcn/dialog.tsx" {...props} />;
    }
);

DialogFooter.displayName = "DialogFooter";

const DialogTitle = memo(
    React.forwardRef<React.ComponentRef<typeof DialogPrimitive.Title>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>>(({ className, ...props }, ref) => <DialogPrimitive.Title ref={ref} className={cn("dw-dialog-title", className)} data-component="DialogTitle" data-location="src/components/ui/shadcn/dialog.tsx" {...props} />)
);

DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = memo(
    React.forwardRef<React.ComponentRef<typeof DialogPrimitive.Description>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>>(({ className, ...props }, ref) => <DialogPrimitive.Description ref={ref} className={cn("dw-dialog-description", className)} data-component="DialogDescription" data-location="src/components/ui/shadcn/dialog.tsx" {...props} />)
);

DialogDescription.displayName = DialogPrimitive.Description.displayName;

export { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger };
